import React from 'react';
import { Paper, Typography, Box, IconButton, Checkbox, FormControlLabel, makeStyles } from '@material-ui/core';
import MapOutlined from '@material-ui/icons/MapOutlined';
import SatelliteOutlined from '@material-ui/icons/SatelliteOutlined';

/**
 * @function useStyles
 * @description Hook for styling using Material-UI's makeStyles. Defines the "Mapa de fundo"
 * panel: the two base-layer buttons and the two option checkboxes share a single row, which fits
 * within the overlay column's width and costs two fewer rows of height than stacking them.
 * `flexWrap` lets the checkboxes drop to a second line rather than overflow, should a narrower
 * column ever be introduced.
 * The checkboxes used to be revealed on hover, mirroring Google Maps' own native map-type
 * control, with the panel capped at 180px to stay compact while collapsed. Both are gone: the
 * panel now lives inside a sized column, so it fills it like every other card and simply shows
 * the options - a hover-only control is easy to miss for what it saved.
 * @param {Object} theme - Material-UI theme containing color, spacing, and other configurations
 * @memberof module:SHARED
 * @returns {Object} Object containing compiled CSS classes for component usage
 */
const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(1, 1.5),
    borderRadius: 8,
    width: '100%',
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    columnGap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
  },
  types: {
    display: 'flex',
    gridGap: 6,
    marginRight: theme.spacing(0.5),
  },
  extraLabel: {
    fontSize: '0.75rem',
    marginLeft: -4,
    marginRight: 0,
  },
  checkbox: {
    padding: theme.spacing(0.5),
  },
}));

/**
 * @function BackgroundMapPanel
 * @description Compact "Mapa de fundo" panel shared by every module's redesigned result page:
 * switches the live Google Maps base layer between roadmap and satellite, replacing that module's
 * native `mapTypeControl` (disabled via `mapOptions={{ mapTypeControl: false }}` on `GoogleMap`).
 * Both option checkboxes are always rendered, for either base layer, so each can be turned on and
 * off independently instead of the panel swapping one for the other. They reach their target
 * through different mechanisms, because Google's base-layer vocabulary is asymmetric:
 * "Marcadores" switches `satellite`<->`hybrid` on the imagery side but needs a style override on
 * the roadmap side (`roadmap`/`terrain` have no label-less variant), while "Relevo" switches
 * `roadmap`<->`terrain` on the roadmap side and has no imagery counterpart at all — Google
 * publishes no relief layer over satellite imagery, so there it renders disabled with a tooltip
 * rather than silently doing nothing.
 * @param {Object} props
 * @param {'roadmap'|'satellite'|'hybrid'|'terrain'} props.mapType - Current Google Maps `mapTypeId`.
 * @param {Function} props.onMapTypeChange - `(mapTypeId) => void`, called with the new `mapTypeId`.
 * @param {Boolean} [props.labelsVisible=true] - Whether roadmap/terrain labels are shown. Only
 *  meaningful on the roadmap side; the imagery side derives it from `mapType` itself.
 * @param {Function} [props.onLabelsVisibleChange] - `(visible: Boolean) => void`. Omitted by a
 *  page that doesn't render label styles, which disables the roadmap-side "Marcadores" box.
 * @param {Function} props.t - `react-i18next`'s translate function.
 * **Called by:
 * @see {ProcessingPage} - every themed module's result page, next to the map's own overlay panels.
 * @memberof module:SHARED
 * @returns {JSX.Element}
 */
const BackgroundMapPanel = ({ mapType, onMapTypeChange, labelsVisible = true, onLabelsVisibleChange, t }) => {
  const classes = useStyles();
  const isSatelliteFamily = mapType === 'satellite' || mapType === 'hybrid';
  const showTerrain = mapType === 'terrain';
  const showLabels = isSatelliteFamily ? mapType === 'hybrid' : labelsVisible;
  const labelsDisabled = !isSatelliteFamily && !onLabelsVisibleChange;

  const handleLabelsChange = (checked) => {
    onLabelsVisibleChange?.(checked);
    if (isSatelliteFamily) onMapTypeChange(checked ? 'hybrid' : 'satellite');
  };

  return (
    <Paper elevation={1} className={classes.root}>
      <Typography variant="caption" color="textSecondary">
        {t('forms.shorelineAnalysis.backgroundMapTitle')}
      </Typography>
      <Box className={classes.controls}>
        <Box className={classes.types}>
          <IconButton
            size="small"
            color={!isSatelliteFamily ? 'primary' : 'default'}
            onClick={() => onMapTypeChange(showTerrain ? 'terrain' : 'roadmap')}
            title={t('forms.shorelineAnalysis.mapTypeMap')}>
            <MapOutlined fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color={isSatelliteFamily ? 'primary' : 'default'}
            onClick={() => onMapTypeChange(showLabels ? 'hybrid' : 'satellite')}
            title={t('forms.shorelineAnalysis.mapTypeSatellite')}>
            <SatelliteOutlined fontSize="small" />
          </IconButton>
        </Box>
        <FormControlLabel
          className={classes.extraLabel}
          disabled={labelsDisabled}
          control={
            <Checkbox
              size="small"
              className={classes.checkbox}
              checked={showLabels}
              onChange={(e) => handleLabelsChange(e.target.checked)}
            />
          }
          label={<Typography variant="caption">{t('forms.shorelineAnalysis.mapTypeLabels')}</Typography>}
        />
        <FormControlLabel
          className={classes.extraLabel}
          disabled={isSatelliteFamily}
          title={isSatelliteFamily ? t('forms.shorelineAnalysis.mapTypeTerrainUnavailable') : undefined}
          control={
            <Checkbox
              size="small"
              className={classes.checkbox}
              checked={showTerrain}
              onChange={(e) => onMapTypeChange(e.target.checked ? 'terrain' : 'roadmap')}
            />
          }
          label={<Typography variant="caption">{t('forms.shorelineAnalysis.mapTypeTerrain')}</Typography>}
        />
      </Box>
    </Paper>
  );
};

export default BackgroundMapPanel;
